import { createSelector } from '@ngrx/store';

import { AppState, AppStore } from '../states/app.state';
import {
  ICopyKeyword,
  IKeyWord,
  IKeywordDefinition,
  SelectedKeywordType,
  IKeywordDefinitionWithUnwrapFlag,
} from '../states';
import { ResultSetPaging } from '@alfresco/js-api';
import { IMarginaliaDeleteDialogData } from '../../model';

export const selectApp = (state: AppStore) => state?.app || ({} as AppState);

export const getUserProfile = createSelector(selectApp, (state) => state?.user);
export const getHeaderTextColor = createSelector(
  selectApp,
  (state) => state?.headerTextColor
);
export const getApsUserProfile = createSelector(
  selectApp,
  (state) => state?.bpmUser
);
export const getAppSelection = createSelector(
  selectApp,
  (state) => state?.selection
);
export const getNavigationState = createSelector(
  selectApp,
  (state) => state?.navigation
);
export const selectCurrentDocument = createSelector(
  getAppSelection,
  (appSelection) =>
    appSelection.nodes?.length ? appSelection.nodes[0].entry : null
);

const getCurrentDocumentNodeId = createSelector(
  selectCurrentDocument,
  (node) => node?.id
);

export const getCurrentDocumentId = createSelector(
  getCurrentDocumentNodeId,
  (nodeId) => nodeId
);

export const isInfoDrawerOpened = createSelector(
  selectApp,
  (state) => state?.infoDrawerOpened
);

export const getDocumentDisplayMode = createSelector(
  selectApp,
  (state) => state?.documentDisplayMode
);

export const isAdmin = createSelector(
  selectApp,
  (state) => state.user?.isAdmin || false
);

export const getCurrentFolder = createSelector(
  selectApp,
  (state) => state.navigation?.currentFolder || null
);

export const selectedFolder = createSelector(selectApp, (state) =>
  state.selection?.folder
    ? state.selection.folder.entry
    : state.navigation?.currentFolder || null
);

export const isSelectedNodeTypeHTML = createSelector(
  getAppSelection,
  (selection) => {
    if (selection.count) {
      const fileName = selection.nodes[0].entry.name;
      if (fileName.search(/.html/i) !== -1) {
        return true;
      }
      return false;
    }
    return false;
  }
);

export const canBatchMetadataUpdate = createSelector(
  getAppSelection,
  (selection) => {
    if (selection.count > 1) {
      return selection.nodes.every((node) => {
        if (!node.entry.isFile) {
          return true;
        }

        return node.entry.aspectNames?.indexOf('cm:workingcopy') === -1;
      });
    }

    if (selection?.first?.entry.isFolder) {
      return true;
    }
    return false;
  }
);

export const sitesSelector = createSelector(
  selectApp,
  (state) => state['sites']
);

export const currentSiteSelector = createSelector(
  selectApp,
  (state) => state['currentSite']
);

export const contentWidgetAssigneeStateSelector = createSelector(
  selectApp,
  (state) => state['contentWidgetAssigneeState']
);

export const currentUseridSelector = createSelector(
  getUserProfile,
  (state) => !!state.id
);

export const currentSiteDocumentRefId = createSelector(
  selectApp,
  (state) => state['siteChildren']
);

export const currentUserSelector = createSelector(
  getUserProfile,
  (state) => state
);

export const isSiteManager = createSelector(
  selectApp,
  (state) => state.isSiteManager
);

export const getRepositoryStatus = createSelector(
  selectApp,
  (state) => state.repository
);

export const getRuleContext = createSelector(
  getAppSelection,
  getNavigationState,
  getUserProfile,
  getRepositoryStatus,
  (selection, navigation, profile, repository) => ({
    selection,
    navigation,
    profile,
    repository,
  })
);

export const getAllKeywordIds = createSelector(
  selectApp,
  (state: AppState): string[] => state.keyWords?.allId || []
);

export const getKeywordById = (id: string) =>
  createSelector(
    selectApp,
    (state: AppState): IKeyWord => state.keyWords?.byId?.[id] || null
  );

export const getAllKeyword = createSelector(
  selectApp,
  (state: AppState): IKeyWord[] => state.keyWords?.allKeyword || []
);

export const getFormattedKeyword = createSelector(
  selectApp,
  (state: AppState): IKeywordDefinition[] =>
    state.keyWords?.formattedKeyword || []
);

export const getSelectedKeyword = createSelector(
  selectApp,
  (state: AppState): SelectedKeywordType => state.keyWords?.selected
);

export const getCopyKeyword = createSelector(
  selectApp,
  (state): ICopyKeyword => state.keyWords?.copyKeyword ?? ({} as ICopyKeyword)
);

export const getDefinitionEditor = createSelector(
  selectApp,
  (state): string | undefined => state.keyWords?.definition
);

export const getKeywordEditor = createSelector(
  selectApp,
  (state): string | undefined => state.keyWords?.keyword
);

export const getSelectedKeywordForDelete = createSelector(
  selectApp,
  (state): IKeywordDefinitionWithUnwrapFlag | undefined =>
    state.keyWords?.keywordForDelete
);

export const getFormattedKeywordById = (id: string) =>
  createSelector(
    getFormattedKeyword,
    (state: IKeywordDefinition[]): IKeywordDefinition =>
      state.find((k) => k.id === id)!
  );

export const selectAllDocs = createSelector(
  selectApp,
  (state) => state.allDocsDashboardSelection
);

export const getCurrentSiteUserRole = createSelector(
  selectApp,
  (state) => state?.currentSiteUserRole
);

export const getCurrentCitations = createSelector(
  selectApp,
  (state: AppState) => state?.citations
);

export const getLatestEditorData = createSelector(
  selectApp,
  (state) => state?.latestEditorContent
);

export const getPaginationData = createSelector(
  selectApp,
  (state): ResultSetPaging => state?.paginationData || ({} as ResultSetPaging)
);

export const getUploadCompleted = createSelector(
  selectApp,
  (state) => state?.uploadCompleted
);
export const getTargetDeleteDialogData = createSelector(
  selectApp,
  (state): IMarginaliaDeleteDialogData => state?.targetDeleteDialogData
);

export const getFeatureFlags = createSelector(
  selectApp,
  (state) => state.featureFlagsState?.flags || {}
);

export const getSelectedDocumentState = createSelector(
  selectApp,
  (state) => state?.selectedDocumentState
);

export const getSelectedRowsDataState = createSelector(
  selectApp,
  (state) => state?.selectedRowsState
);
